/**
 * Curated catalog of the analytics events the Sulaco frontend emits.
 *
 * Event names are human-readable, Title Case, past-tense-ish strings so they
 * read well in Mixpanel's UI (matching the demo's convention, e.g. the demo's
 * "Channel Changed" / "Demo Viewed"). Import from here instead of inlining
 * string literals so names stay consistent and greppable.
 *
 * Property naming: camelCase keys, primitive values. Slow-moving session
 * context (org, role) is registered as Mixpanel super properties in
 * useAnalytics and does NOT need to be passed per-event.
 */
export const AnalyticsEvents = {
    // --- Navigation ---------------------------------------------------------
    /** Fired on every route change. Props: { screen, path, pattern }. */
    PageViewed: 'Page Viewed',

    // --- Authentication / session ------------------------------------------
    SignInSubmitted: 'Sign In Submitted',
    SignInSucceeded: 'Sign In Succeeded',
    SignInFailed: 'Sign In Failed',
    LoggedOut: 'Logged Out',
    PasswordRecoveryRequested: 'Password Recovery Requested',
    PasswordReset: 'Password Reset',
    InviteAccepted: 'Invite Accepted',

    // --- Global chrome / actions -------------------------------------------
    GetStartedOpened: 'Get Started Opened',
    GetStartedItemClicked: 'Get Started Item Clicked',
    ColorSchemeToggled: 'Color Scheme Toggled',

    // --- Studio: components -------------------------------------------------
    ComponentCreated: 'Component Created',
    ComponentUpdated: 'Component Updated',
    ComponentDeleted: 'Component Deleted',
    /** A reload/refresh push to devices showing a component. Props: { scope }. */
    ComponentReloadRequested: 'Component Reload Requested',

    // --- Studio: scenes -----------------------------------------------------
    /** Props: { source, templateId, panelCount }. */
    SceneCreated: 'Scene Created',
    SceneUpdated: 'Scene Updated',
    SceneDeleted: 'Scene Deleted',

    // --- Studio: playlists --------------------------------------------------
    PlaylistCreated: 'Playlist Created',
    /**
     * Any change to a playlist. Props: { source, action } where source is
     * 'playlist_editor' (edited in the studio) or 'device' (a device's playlist
     * assignment changed — the device side).
     */
    PlaylistUpdated: 'Playlist Updated',
    PlaylistDeleted: 'Playlist Deleted',

    // --- Device screen preview (Devices page) -------------------------------
    /** The full-screen scene preview opened from a device card. */
    ScreenPreviewOpened: 'Screen Preview Opened',
    /** Navigating between scenes in the preview. Props: { method, index }. */
    ScreenPreviewSceneChanged: 'Screen Preview Scene Changed',
    /** "Edit scene" tapped from the preview, opening the editor drawer over it. */
    SceneEditOpened: 'Scene Edit Opened',
    /** The trailing "Add scene" slide tapped, opening the add-scene picker. */
    SceneAddOpened: 'Scene Add Opened',

    // --- Org / members ------------------------------------------------------
    MemberInvited: 'Member Invited',
    MemberRemoved: 'Member Removed',
    ProfileUpdated: 'Profile Updated',

    // --- Marketing surfaces -------------------------------------------------
    MarketplaceItemClicked: 'Marketplace Item Clicked',
    PitchOpened: 'Pitch Opened',
    DemoOpened: 'Demo Opened',
} as const;

export type AnalyticsEventName =
    (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];
